import React from 'react'
import { useSettings } from '../contexts/SettingsContext'
import { isKioskSettingsHost, useKioskRole } from '../hooks/useKioskRole'
import {
  faEye,
  faEyeSlash,
  faPalette,
  faShieldHalved,
  faGear,
  faChevronDown,
  faDisplay,
  faRotateRight,
  faBomb,
  faPowerOff,
  faServer,
  faXmark,
  faBell,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import MFAModal from './MFAModal';
import { useConfirmPassword } from './ConfirmPasswordModal';
import { executeCapability } from '../utils/capabilityExecute'
import { sendLocalPushNotification } from '../utils/localPushNotification'
import {
  disableWebPush,
  enableWebPush,
  getPushNotificationsEnabled,
  readWebPushPermission,
  type WebPushPermission,
} from '../utils/webPushSubscribe'
import {
  requestKioskDisplayRefresh,
  requestKioskDisplayHardRefresh,
  requestKioskAgentRestart,
  requestKioskDeviceReboot,
  kioskSoftReload,
  kioskHardReload,
  fetchKioskAgentHealth,
  getKioskAgentBaseUrl,
  type KioskAgentHealth,
} from '../utils/kioskDisplaySync'
import { isInsideKioskVirtualKeyboard } from '../utils/kioskVirtualKeyboard'

interface SettingsModalProps {
  open: boolean
  onClose: () => void
}

type SettingsCategoryId = 'appearance' | 'notifications' | 'kiosk' | 'security' | 'advanced'

const SETTINGS_CATEGORIES: Array<{
  id: SettingsCategoryId
  label: string
  icon: IconDefinition
}> = [
  { id: 'appearance', label: 'Appearance', icon: faPalette },
  { id: 'notifications', label: 'Notifications', icon: faBell },
  { id: 'kiosk', label: 'Kiosk', icon: faDisplay },
  { id: 'security', label: 'Security', icon: faShieldHalved },
  { id: 'advanced', label: 'Advanced', icon: faGear },
]

function pushPermissionLabel(permission: WebPushPermission): string {
  if (permission === 'granted') return 'Allowed'
  if (permission === 'denied') return 'Blocked'
  if (permission === 'unsupported') return 'Not supported'
  return 'Not yet asked'
}

function SettingsCategoryDropdown({
  activeCategory,
  onChange,
  categories = SETTINGS_CATEGORIES,
}: {
  activeCategory: SettingsCategoryId
  onChange: (id: SettingsCategoryId) => void
  categories?: typeof SETTINGS_CATEGORIES
}) {
  const [menuOpen, setMenuOpen] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const active =
    categories.find((category) => category.id === activeCategory) ??
    categories[0]

  React.useEffect(() => {
    if (!menuOpen) return undefined

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    const handleClick = (event: MouseEvent) => {
      if (isInsideKioskVirtualKeyboard(event.target)) return
      if (!containerRef.current?.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('click', handleClick)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('click', handleClick)
    }
  }, [menuOpen])

  return (
    <div className="relative min-w-0 flex-1" ref={containerRef}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={menuOpen}
        aria-label="Settings category"
        onClick={() => setMenuOpen((value) => !value)}
        className="flex h-10 w-full min-w-0 items-center gap-2 rounded-lg px-2 text-left transition-colors hover:bg-base-200"
      >
        <FontAwesomeIcon icon={active.icon} className="w-4 shrink-0 text-center" />
        <span className="min-w-0 flex-1 truncate text-base font-semibold">
          {active.label}
        </span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`w-3 shrink-0 opacity-60 transition-transform ${
            menuOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {menuOpen ? (
        <ul
          role="listbox"
          aria-label="Settings categories"
          className="menu absolute top-full right-0 left-0 z-50 mt-1 max-h-[min(24rem,calc(100vh-8rem))] overflow-y-auto rounded-box border border-base-300 bg-base-100 p-1 shadow-lg"
          onClick={(event) => event.stopPropagation()}
        >
          {categories.map((category) => {
            const selected = category.id === activeCategory
            return (
              <li key={category.id} role="none">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    onChange(category.id)
                    setMenuOpen(false)
                  }}
                  className={`flex w-full items-center gap-3 text-sm ${
                    selected ? 'active font-medium' : ''
                  }`}
                >
                  <FontAwesomeIcon
                    icon={category.icon}
                    className="w-4 shrink-0 text-center"
                  />
                  <span className="truncate">{category.label}</span>
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}

const SettingsModal: React.FC<SettingsModalProps> = ({ open, onClose }) => {
  const {
    aiAssistant,
    showAppDescriptions,
    controllerUiScale,
    displayUiScale,
    forceVirtualKeyboard,
    toggleAIAssistant,
    toggleShowAppDescriptions,
    toggleForceVirtualKeyboard,
    openSleepPreview,
    setControllerUiScale,
    setDisplayUiScale,
  } = useSettings()
  const { logout, hasPermission } = useAuth();
  const isAdmin = hasPermission('adminPage');
  const kioskRole = useKioskRole()
  const kioskSettingsHost = isKioskSettingsHost(kioskRole)
  const visibleCategories = React.useMemo(
    () =>
      kioskSettingsHost
        ? SETTINGS_CATEGORIES.filter((c) => c.id === 'appearance' || c.id === 'kiosk')
        : SETTINGS_CATEGORIES.filter((c) => c.id !== 'kiosk'),
    [kioskSettingsHost]
  )
  const { confirmPassword: requestPasswordConfirm, confirmPasswordModal } = useConfirmPassword()
  const [mfaStatus, setMfaStatus] = React.useState<{ enabled: boolean; remainingRecoveryCodes: number; passkeyCount?: number; methods?: string[] } | null>(null)
  const [, setMfaSetup] = React.useState<{ otpauth: string; secretPreview: string } | null>(null)
  const [, setMfaCode] = React.useState('')
  const [recoveryCodes, setRecoveryCodes] = React.useState<string[] | null>(null)
  const [, setRelinking] = React.useState(false)
  const [showMfaModal, setShowMfaModal] = React.useState(false)
  const [mfaOtpauthURI, setMfaOtpauthURI] = React.useState<string>('')
  const [passkeys, setPasskeys] = React.useState<Array<{ credentialId: string; createdAt?: string; lastUsedAt?: string; nickname?: string | null }>>([])
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [showCurrent, setShowCurrent] = React.useState(false);
  const [showNew, setShowNew] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState('');
  const [error, setError] = React.useState('');
  const [pushTestBusy, setPushTestBusy] = React.useState(false);
  const [pushTestSuccess, setPushTestSuccess] = React.useState('');
  const [pushTestError, setPushTestError] = React.useState('');
  const [pushEnabled, setPushEnabled] = React.useState(true);
  const [pushPrefLoaded, setPushPrefLoaded] = React.useState(false);
  const [pushPrefBusy, setPushPrefBusy] = React.useState(false);
  const [pushPrefError, setPushPrefError] = React.useState('');
  const [pushPermission, setPushPermission] = React.useState<WebPushPermission>(() => readWebPushPermission());
  const [kioskActionBusy, setKioskActionBusy] = React.useState<string | null>(null)
  const [kioskActionError, setKioskActionError] = React.useState('')
  const [kioskActionSuccess, setKioskActionSuccess] = React.useState('')
  const [kioskHealth, setKioskHealth] = React.useState<KioskAgentHealth | null>(null)
  const [kioskHealthError, setKioskHealthError] = React.useState<string | null>(null)
  const [kioskHealthChecking, setKioskHealthChecking] = React.useState(false)
  const [kioskHealthCheckedAt, setKioskHealthCheckedAt] = React.useState<number | null>(null)
  const [authConfig, setAuthConfig] = React.useState({
    mfaRememberDays: 7,
    sessionInactivityTimeoutMinutes: 60
  });
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  const [theme, setTheme] = React.useState(() =>
    typeof document !== 'undefined' ? (document.documentElement.getAttribute('data-theme') || localStorage.getItem('theme') || 'light') : 'light'
  );
  const [activeCategory, setActiveCategory] = React.useState<SettingsCategoryId>('appearance')
  const activeCategoryLabel =
    visibleCategories.find((category) => category.id === activeCategory)?.label ?? 'Settings'

  React.useEffect(() => {
    if (!visibleCategories.some((c) => c.id === activeCategory)) {
      setActiveCategory(visibleCategories[0]?.id ?? 'appearance')
    }
  }, [visibleCategories, activeCategory])

  const refreshKioskHealth = React.useCallback(async () => {
    setKioskHealthChecking(true)
    try {
      const health = await fetchKioskAgentHealth()
      setKioskHealth(health)
      setKioskHealthError(null)
      setKioskHealthCheckedAt(Date.now())
    } catch (err: unknown) {
      setKioskHealth(null)
      setKioskHealthError(err instanceof Error ? err.message : 'Unreachable')
      setKioskHealthCheckedAt(Date.now())
    } finally {
      setKioskHealthChecking(false)
    }
  }, [])

  // Poll agent health while Kiosk settings are open
  React.useEffect(() => {
    if (!open || !kioskSettingsHost || activeCategory !== 'kiosk') return
    void refreshKioskHealth()
    const id = window.setInterval(() => {
      void refreshKioskHealth()
    }, 5000)
    return () => window.clearInterval(id)
  }, [open, kioskSettingsHost, activeCategory, refreshKioskHealth])

  const enabledThemes = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'cupcake', label: 'Cupcake' },
    { value: 'bumblebee', label: 'Bumblebee' },
    { value: 'emerald', label: 'Emerald' },
    { value: 'corporate', label: 'Corporate' },
    { value: 'synthwave', label: 'Synthwave' },
    { value: 'retro', label: 'Retro' },
    { value: 'cyberpunk', label: 'Cyberpunk' },
    { value: 'valentine', label: 'Valentine' },
    { value: 'halloween', label: 'Halloween' },
    { value: 'garden', label: 'Garden' },
    { value: 'forest', label: 'Forest' },
    { value: 'aqua', label: 'Aqua' },
    { value: 'lofi', label: 'LoFi' },
    { value: 'pastel', label: 'Pastel' },
    { value: 'fantasy', label: 'Fantasy' },
    { value: 'wireframe', label: 'Wireframe' },
    { value: 'black', label: 'Black' },
    { value: 'luxury', label: 'Luxury' },
    { value: 'dracula', label: 'Dracula' },
    { value: 'cmyk', label: 'CMYK' },
    { value: 'autumn', label: 'Autumn' },
    { value: 'business', label: 'Business' },
    { value: 'acid', label: 'Acid' },
    { value: 'lemonade', label: 'Lemonade' },
    { value: 'night', label: 'Night' },
    { value: 'coffee', label: 'Coffee' },
    { value: 'winter', label: 'Winter' },
    { value: 'dim', label: 'Dim' },
    { value: 'nord', label: 'Nord' },
    { value: 'sunset', label: 'Sunset' },
    { value: 'caramellatte', label: 'Caramel Latte' },
    { value: 'abyss', label: 'Abyss' },
    { value: 'silk', label: 'Silk' },
  ] as const;

  const currentTheme = enabledThemes.find(t => t.value === theme)?.label ?? 'Theme';

  const applyTheme = (value: string) => {
    if (typeof document === 'undefined') return;
    setTheme(value);
    document.documentElement.setAttribute('data-theme', value);
    localStorage.setItem('theme', value);
    window.dispatchEvent(new CustomEvent('lts-theme-change', { detail: { theme: value } }));
  };

  const ThemePreviewSwatch: React.FC<{ themeValue: string; outerClassName: string }> = ({ themeValue, outerClassName }) => (
    <div data-theme={themeValue} className={outerClassName} aria-hidden>
      <div className="bg-base-content size-1 rounded-full" />
      <div className="bg-primary size-1 rounded-full" />
      <div className="bg-secondary size-1 rounded-full" />
      <div className="bg-accent size-1 rounded-full" />
    </div>
  );

  const formatRemaining = (remainingMs: number | null, fallback: string): string => {
    if (remainingMs === null) return fallback;
    if (remainingMs <= 0) return 'Expired';
    const totalSeconds = Math.floor(remainingMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    return `${minutes}m ${seconds}s`;
  };

  const getTokenExpiryMs = (token: string | null): number | null => {
    if (!token) return null;
    try {
      const decoded = jwtDecode<{ exp?: number }>(token);
      return decoded.exp ? decoded.exp * 1000 : null;
    } catch {
      return null;
    }
  };

  const passwordRequirements = [
    { label: 'At least 8 characters', test: (pw: string) => pw.length >= 8 },
    { label: 'One uppercase letter', test: (pw: string) => /[A-Z]/.test(pw) },
    { label: 'One lowercase letter', test: (pw: string) => /[a-z]/.test(pw) },
    { label: 'One number', test: (pw: string) => /[0-9]/.test(pw) },
    { label: 'One special character', test: (pw: string) => /[^A-Za-z0-9]/.test(pw) },
  ];
  function getPasswordErrors(pw: string) {
    return passwordRequirements.filter(req => !req.test(pw)).map(req => req.label);
  }
  const passwordValid = newPassword && getPasswordErrors(newPassword).length === 0;
  const canSubmit = currentPassword && newPassword && confirmPassword && passwordValid && newPassword === confirmPassword && !loading;

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!canSubmit) return;
    setLoading(true);
    try {
      const out = await executeCapability<{ message?: string }>({
        app: 'global',
        capability: 'users.changeMyPassword',
        input: { currentPassword, newPassword, confirmPassword },
      })
      setSuccess(out?.message || 'Password changed successfully!')
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to change password';
      setError(message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    if (open) {
      setActiveCategory('appearance')
      setPushTestSuccess('')
      setPushTestError('')
      setPushPrefError('')
      authService.mfaStatus().then(setMfaStatus).catch(() => setMfaStatus(null))
      authService.mfaPasskeys().then(setPasskeys).catch(() => setPasskeys([]))
      authService.getAuthConfig()
        .then(cfg => {
          setAuthConfig({
            mfaRememberDays: cfg.mfaRememberDays > 0 ? cfg.mfaRememberDays : 7,
            sessionInactivityTimeoutMinutes: cfg.sessionInactivityTimeoutMinutes > 0 ? cfg.sessionInactivityTimeoutMinutes : 60
          });
        })
        .catch(() => {
          setAuthConfig({ mfaRememberDays: 7, sessionInactivityTimeoutMinutes: 60 });
        });
      setTheme(document.documentElement.getAttribute('data-theme') || localStorage.getItem('theme') || 'light')
    }
  }, [open])

  React.useEffect(() => {
    if (!open) return;
    setNowMs(Date.now());
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [open]);

  React.useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, [open]);

  React.useEffect(() => {
    if (!open || kioskSettingsHost) return undefined
    let cancelled = false
    setPushPermission(readWebPushPermission())
    void (async () => {
      try {
        const enabled = await getPushNotificationsEnabled()
        if (!cancelled) setPushEnabled(enabled)
      } catch (err: unknown) {
        if (!cancelled) {
          setPushPrefError(err instanceof Error ? err.message : 'Failed to load notification settings')
        }
      } finally {
        if (!cancelled) setPushPrefLoaded(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, kioskSettingsHost])

  const clearMfaCacheAndLogout = () => {
    if (!confirm('Clear remembered MFA on this device and log out now?')) return;
    authService.setRememberDeviceToken(null);
    logout();
    onClose();
  };

  const downloadRecoveryCodes = (codes: string[]) => {
    if (!codes?.length) return;
    const date = new Date().toISOString().slice(0, 10);
    const content = [
      'LTS Portal Recovery Codes',
      `Generated: ${new Date().toISOString()}`,
      '',
      'Store these one-time codes in a safe location.',
      'Each code can be used only once.',
      '',
      ...codes
    ].join('\n');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lts-recovery-codes-${date}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const sessionExpiryMs = getTokenExpiryMs(localStorage.getItem('lts_jwt_token'));
  const rememberDeviceExpiryMs = getTokenExpiryMs(authService.getRememberDeviceToken());
  const lastActivityRaw = localStorage.getItem('lts_last_activity_at');
  const lastActivityMs = lastActivityRaw ? Number(lastActivityRaw) : NaN;
  const inactivityTimeoutMs = authConfig.sessionInactivityTimeoutMinutes * 60 * 1000;
  const inactivityExpiryMs = Number.isFinite(lastActivityMs) ? (lastActivityMs + inactivityTimeoutMs) : null;

  const hardLogoutRemaining = sessionExpiryMs !== null ? sessionExpiryMs - nowMs : null;
  const inactivityRemaining = inactivityExpiryMs !== null ? inactivityExpiryMs - nowMs : null;
  const mfaRememberRemaining = rememberDeviceExpiryMs !== null ? rememberDeviceExpiryMs - nowMs : null;
  const totpConfigured = !!mfaStatus?.methods?.includes('totp');
  const passkeyCount = Number.isFinite(Number(mfaStatus?.passkeyCount)) ? Number(mfaStatus?.passkeyCount) : passkeys.length;
  const passkeysConfigured = passkeyCount > 0;

  if (!open) return null

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  const handleSendTestPush = async (delayMs: number) => {
    if (pushTestBusy) return;
    setPushTestBusy(true);
    setPushTestSuccess('');
    setPushTestError('');
    try {
      const result = await sendLocalPushNotification({ delayMs });
      if (delayMs > 0) {
        setPushTestSuccess(
          `Notification scheduled in ${Math.round(delayMs / 1000)}s via ${result.via === 'service-worker' ? 'service worker' : 'page'}. You can background this tab to test.`
        );
      } else {
        setPushTestSuccess(
          `Notification sent via ${result.via === 'service-worker' ? 'service worker' : 'page'}.`
        );
      }
    } catch (err: unknown) {
      setPushTestError(err instanceof Error ? err.message : 'Failed to send notification');
    } finally {
      setPushTestBusy(false);
    }
  };

  const handlePushEnabledChange = async (next: boolean) => {
    if (pushPrefBusy) return
    setPushPrefBusy(true)
    setPushPrefError('')
    try {
      if (next) {
        const result = await enableWebPush()
        const permission = result.permission || readWebPushPermission()
        setPushPermission(permission)
        setPushEnabled(result.enabled)
        if (!result.ok) {
          if (permission === 'denied') {
            setPushPrefError(
              'Notifications are blocked in this browser. Allow them in your browser or device settings, then turn push back on.'
            )
          } else if (permission === 'default') {
            setPushPrefError('The browser did not show a permission prompt. Use Allow notifications to try again.')
          } else {
            setPushPrefError(result.reason || 'Failed to enable push notifications')
          }
        }
      } else {
        await disableWebPush()
        setPushEnabled(false)
        setPushPermission(readWebPushPermission())
      }
    } catch (err: unknown) {
      setPushPrefError(err instanceof Error ? err.message : 'Failed to update push notifications')
      setPushPermission(readWebPushPermission())
    } finally {
      setPushPrefBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex bg-black bg-opacity-40" onClick={handleOverlayClick}>
      <div className="bg-base-100 shadow-lg w-full h-full flex flex-col overflow-hidden pt-safe">
        <header className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-base-300 px-4">
          <h2 className="hidden text-lg font-semibold text-base-content md:block">Settings</h2>
          <div className="min-w-0 flex-1 md:hidden">
            <SettingsCategoryDropdown
              activeCategory={activeCategory}
              onChange={setActiveCategory}
              categories={visibleCategories}
            />
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm btn-square hidden md:inline-flex"
            aria-label="Close settings"
            onClick={onClose}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </header>
        <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
          <aside className="hidden w-60 shrink-0 flex-col border-r border-base-300 bg-base-200/50 p-3 md:flex">
            <nav aria-label="Settings categories" className="flex flex-col gap-1">
              {visibleCategories.map((category) => {
                const active = category.id === activeCategory
                return (
                  <button
                    key={category.id}
                    type="button"
                    aria-current={active ? 'page' : undefined}
                    onClick={() => setActiveCategory(category.id)}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                      active
                        ? 'bg-base-100 font-medium text-base-content shadow-sm'
                        : 'text-base-content/70 hover:bg-base-100/60 hover:text-base-content'
                    }`}
                  >
                    <FontAwesomeIcon icon={category.icon} className="w-4 shrink-0 text-center" />
                    <span className="truncate">{category.label}</span>
                  </button>
                )
              })}
            </nav>
          </aside>
          <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain">
            <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
              <p className="mb-6 hidden text-xs font-semibold uppercase tracking-wide text-base-content/40 md:block">
                {activeCategoryLabel}
              </p>
        {activeCategory === 'appearance' && (
          <>
        {/* Theme Picker */}
        <div className="mb-6">
          <label className="block font-semibold mb-2">Theme</label>
          <div className="dropdown dropdown-end inline-block">
            <button
              type="button"
              tabIndex={0}
              role="button"
              className="btn group btn-sm gap-2 px-2 btn-ghost"
              aria-label="Change Theme"
            >
              <ThemePreviewSwatch
                themeValue={theme}
                outerClassName="bg-base-100 group-hover:border-base-content/20 border-base-content/10 grid shrink-0 grid-cols-2 gap-0.5 rounded-md border p-1 transition-colors"
              />
              <span className="text-xs font-medium truncate max-w-[6.5rem]">{currentTheme}</span>
              <svg
                width="12px"
                height="12px"
                className="mt-px hidden size-2 fill-current opacity-60 sm:inline-block"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 2048 2048"
                aria-hidden
              >
                <path d="M1799 349l242 241-1017 1017L7 590l242-241 775 775 775-775z" />
              </svg>
            </button>
            <div
              tabIndex={0}
              className="dropdown-content bg-base-200 text-base-content rounded-box mt-2 h-[30.5rem] max-h-[calc(100vh-6.5rem)] overflow-y-auto border-[length:var(--border)] border-white/5 shadow-2xl outline-[length:var(--border)] outline-black/5"
              style={{ left: 0, right: 'auto' }}
            >
              <ul className="menu w-56">
                <li className="menu-title text-xs">Theme</li>
                {enabledThemes.map(t => (
                  <li key={t.value}>
                    <button
                      type="button"
                      className="gap-3 px-2"
                      data-set-theme={t.value}
                      aria-pressed={theme === t.value}
                      onClick={() => applyTheme(t.value)}
                    >
                      <ThemePreviewSwatch
                        themeValue={t.value}
                        outerClassName="bg-base-100 grid shrink-0 grid-cols-2 gap-0.5 rounded-md p-1 shadow-sm"
                      />
                      <div className="w-32 truncate">{t.label}</div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className={theme === t.value ? 'h-3 w-3 shrink-0' : 'invisible h-3 w-3 shrink-0'}
                        aria-hidden
                      >
                        <path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z" />
                      </svg>
                    </button>
                  </li>
                ))}
                <li />
              </ul>
            </div>
          </div>
        </div>
        {/* Dashboard */}
        <div className="mb-6">
          <label className="block font-semibold mb-2">Dashboard</label>
          <div className="form-control">
            <label className="label cursor-pointer">
              <span className="label-text text-base-content font-medium">Show app descriptions</span>
              <input
                type="checkbox"
                className="toggle"
                checked={showAppDescriptions}
                onChange={toggleShowAppDescriptions}
              />
            </label>
            <p className="text-xs text-base-content/60 ml-4">
              Show or hide subtitle text on Internal and External app cards.
            </p>
          </div>
        </div>
        {kioskSettingsHost && (
          <div className="mb-6">
            <label className="block font-semibold mb-2">UI scale</label>
            <p className="text-xs text-base-content/60 mb-3">
              Controller scale applies on this touchscreen. Display scale is pushed to the TV browser.
            </p>
            <div className="form-control mb-4">
              <label className="label py-1">
                <span className="label-text font-medium">Controller</span>
                <span className="label-text-alt tabular-nums">{Math.round(controllerUiScale * 100)}%</span>
              </label>
              <input
                type="range"
                min={50}
                max={175}
                step={5}
                className="range range-primary range-sm"
                value={Math.round(controllerUiScale * 100)}
                onChange={(e) => setControllerUiScale(Number(e.target.value) / 100)}
              />
            </div>
            <div className="form-control">
              <label className="label py-1">
                <span className="label-text font-medium">Display</span>
                <span className="label-text-alt tabular-nums">{Math.round(displayUiScale * 100)}%</span>
              </label>
              <input
                type="range"
                min={50}
                max={175}
                step={5}
                className="range range-primary range-sm"
                value={Math.round(displayUiScale * 100)}
                onChange={(e) => setDisplayUiScale(Number(e.target.value) / 100)}
              />
            </div>
          </div>
        )}
          </>
        )}
        {activeCategory === 'notifications' && !kioskSettingsHost && (
          <div className="mb-6">
            <label className="block font-semibold mb-2">Push notifications</label>
            <div className="rounded-lg border border-base-300 bg-base-100 p-4 space-y-3">
              <p className="text-xs text-base-content/60">
                On by default. When your access level has a notification set to push, this device shows an OS notification even if the portal is closed. On iPhone and iPad that works after you add the portal to your Home Screen (iOS 16.4 or later).
              </p>
              {pushPrefError && <div className="alert alert-error py-2 px-3 text-sm">{pushPrefError}</div>}
              {pushPermission === 'denied' && (
                <p className="text-xs text-error">
                  Notifications are blocked in this browser. Allow them in your browser or device settings. This toggle cannot override that block.
                </p>
              )}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-sm">Push notifications</div>
                  <div className="text-xs text-base-content/60">
                    Browser permission: {pushPermissionLabel(pushPermission)}
                  </div>
                </div>
                <input
                  type="checkbox"
                  className="toggle toggle-primary shrink-0"
                  checked={pushEnabled}
                  disabled={!pushPrefLoaded || pushPrefBusy}
                  aria-label="Push notifications"
                  onChange={(e) => void handlePushEnabledChange(e.target.checked)}
                />
              </div>
              {pushEnabled && pushPermission === 'default' && (
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={pushPrefBusy}
                  onClick={() => void handlePushEnabledChange(true)}
                >
                  {pushPrefBusy ? 'Requesting…' : 'Allow notifications'}
                </button>
              )}
            </div>
          </div>
        )}
        {activeCategory === 'kiosk' && kioskSettingsHost && (
          <>
            <div className="mb-6">
              <div className="mb-2 flex items-center justify-between gap-2">
                <label className="block font-semibold">Host agent</label>
                <button
                  type="button"
                  className="btn btn-ghost btn-xs gap-1"
                  disabled={kioskHealthChecking}
                  onClick={() => void refreshKioskHealth()}
                >
                  <FontAwesomeIcon icon={faRotateRight} className={kioskHealthChecking ? 'animate-spin' : ''} />
                  Check
                </button>
              </div>
              <div
                className={`rounded-lg border p-3 ${
                  kioskHealth
                    ? 'border-success/40 bg-success/10'
                    : kioskHealthError
                      ? 'border-error/40 bg-error/10'
                      : 'border-base-300 bg-base-200/40'
                }`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-block h-2.5 w-2.5 rounded-full ${
                      kioskHealth ? 'bg-success' : kioskHealthError ? 'bg-error' : 'bg-base-content/30'
                    }`}
                    aria-hidden
                  />
                  <span className="font-medium">
                    {kioskHealth
                      ? 'Connected'
                      : kioskHealthChecking && !kioskHealthCheckedAt
                        ? 'Checking…'
                        : 'Disconnected'}
                  </span>
                  <span className="text-xs text-base-content/50">{getKioskAgentBaseUrl()}</span>
                </div>
                {kioskHealth ? (
                  <dl className="mt-2 grid grid-cols-1 gap-1 text-xs text-base-content/70 sm:grid-cols-2">
                    <div>
                      <dt className="inline text-base-content/50">Version </dt>
                      <dd className="inline font-mono">{kioskHealth.version || '—'}</dd>
                    </div>
                    <div>
                      <dt className="inline text-base-content/50">Topology </dt>
                      <dd className="inline">
                        {kioskHealth.topology || '—'}
                        {typeof kioskHealth.monitorCount === 'number'
                          ? ` (${kioskHealth.monitorCount} screen${kioskHealth.monitorCount === 1 ? '' : 's'})`
                          : ''}
                      </dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="inline text-base-content/50">Browsers </dt>
                      <dd className="inline">
                        {(kioskHealth.launchedBrowsers || []).length > 0
                          ? (kioskHealth.launchedBrowsers || [])
                              .map((b) => `${b.role || b.name || '?'}${b.monitor ? `@${b.monitor}` : ''}`)
                              .join(', ')
                          : 'none reported'}
                      </dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="inline text-base-content/50">Device auth </dt>
                      <dd className="inline">
                        {kioskHealth.config?.deviceAuthEnabled ? 'enabled' : 'disabled'}
                        {kioskHealth.config?.portalBaseUrl
                          ? ` → ${kioskHealth.config.portalBaseUrl}`
                          : ''}
                      </dd>
                    </div>
                    {kioskHealth.startedAt ? (
                      <div className="sm:col-span-2">
                        <dt className="inline text-base-content/50">Started </dt>
                        <dd className="inline">
                          {new Date(kioskHealth.startedAt).toLocaleString()}
                        </dd>
                      </div>
                    ) : null}
                  </dl>
                ) : (
                  <p className="mt-2 text-xs text-base-content/70">
                    {kioskHealthError
                      ? `Cannot reach agent (${kioskHealthError}). Is lts-kiosk-linux running?`
                      : 'Waiting for health check…'}
                  </p>
                )}
                {kioskHealthCheckedAt ? (
                  <p className="mt-2 text-[10px] text-base-content/40">
                    Last check {new Date(kioskHealthCheckedAt).toLocaleTimeString()}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="mb-6">
              <label className="block font-semibold mb-2">Displays</label>
              <p className="text-xs text-base-content/60 mb-3">
                Soft reload keeps cache. Hard refresh clears browser caches and reloads every screen.
              </p>
              {kioskActionSuccess ? (
                <div className="alert alert-success py-2 px-3 text-sm mb-3">{kioskActionSuccess}</div>
              ) : null}
              {kioskActionError ? (
                <div className="alert alert-error py-2 px-3 text-sm mb-3">{kioskActionError}</div>
              ) : null}
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline btn-sm gap-2"
                  disabled={!!kioskActionBusy}
                  onClick={() => {
                    void (async () => {
                      setKioskActionBusy('refresh')
                      setKioskActionError('')
                      setKioskActionSuccess('')
                      try {
                        await requestKioskDisplayRefresh(kioskRole || 'controller')
                        await new Promise((r) => setTimeout(r, 150))
                        setKioskActionSuccess('Refreshing…')
                        kioskSoftReload()
                      } catch (err: unknown) {
                        setKioskActionError(err instanceof Error ? err.message : 'Refresh failed')
                        setKioskActionBusy(null)
                      }
                    })()
                  }}
                >
                  <FontAwesomeIcon icon={faRotateRight} />
                  {kioskActionBusy === 'refresh' ? 'Refreshing…' : 'Refresh all displays'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline btn-sm gap-2"
                  disabled={!!kioskActionBusy}
                  onClick={() => {
                    void (async () => {
                      setKioskActionBusy('hard')
                      setKioskActionError('')
                      setKioskActionSuccess('')
                      try {
                        await requestKioskDisplayHardRefresh(kioskRole || 'controller')
                        await new Promise((r) => setTimeout(r, 150))
                        setKioskActionSuccess('Hard refreshing…')
                        await kioskHardReload()
                      } catch (err: unknown) {
                        setKioskActionError(err instanceof Error ? err.message : 'Hard refresh failed')
                        setKioskActionBusy(null)
                      }
                    })()
                  }}
                >
                  <FontAwesomeIcon icon={faBomb} />
                  {kioskActionBusy === 'hard' ? 'Hard refreshing…' : 'Hard refresh all displays'}
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label className="block font-semibold mb-2">Restart agent</label>
              <p className="text-xs text-base-content/60 mb-3">
                Stop → pull updates from git → start. Browsers relaunch with a fresh device session.
              </p>
              <button
                type="button"
                className="btn btn-warning btn-sm gap-2"
                disabled={!!kioskActionBusy}
                onClick={() => {
                  if (
                    !window.confirm(
                      'Restart the kiosk agent now? Screens will go dark briefly while it updates and relaunches.',
                    )
                  ) {
                    return
                  }
                  void (async () => {
                    setKioskActionBusy('agent')
                    setKioskActionError('')
                    setKioskActionSuccess('')
                    try {
                      await requestKioskAgentRestart()
                      setKioskActionSuccess(
                        'Agent restart started. This screen will reconnect when the service is back.',
                      )
                      window.setTimeout(() => void refreshKioskHealth(), 3000)
                    } catch (err: unknown) {
                      setKioskActionError(err instanceof Error ? err.message : 'Agent restart failed')
                    } finally {
                      setKioskActionBusy(null)
                    }
                  })()
                }}
              >
                <FontAwesomeIcon icon={faServer} />
                {kioskActionBusy === 'agent' ? 'Restarting…' : 'Restart kiosk agent'}
              </button>
            </div>

            <div className="mb-2">
              <label className="block font-semibold mb-2">Device</label>
              <p className="text-xs text-base-content/60 mb-3">
                Full machine reboot. Requires the kiosk user to be allowed to reboot without a password (e.g. systemd/polkit).
              </p>
              <button
                type="button"
                className="btn btn-error btn-sm gap-2"
                disabled={!!kioskActionBusy}
                onClick={() => {
                  if (
                    !window.confirm(
                      'Reboot this device now? All screens will go offline until it boots again.',
                    )
                  ) {
                    return
                  }
                  void (async () => {
                    setKioskActionBusy('reboot')
                    setKioskActionError('')
                    setKioskActionSuccess('')
                    try {
                      await requestKioskDeviceReboot()
                      setKioskActionSuccess('Reboot requested…')
                    } catch (err: unknown) {
                      setKioskActionError(err instanceof Error ? err.message : 'Reboot failed')
                      setKioskActionBusy(null)
                    }
                  })()
                }}
              >
                <FontAwesomeIcon icon={faPowerOff} />
                {kioskActionBusy === 'reboot' ? 'Rebooting…' : 'Reboot device'}
              </button>
            </div>
          </>
        )}
        {activeCategory === 'advanced' && !kioskSettingsHost && (
          <>
        {/* Kiosk UI previews (desktop testing) */}
        <div className="mb-6">
          <label className="block font-semibold mb-2">Kiosk UI previews</label>
          <div className="rounded-lg border border-base-300 bg-base-100 p-4 space-y-4">
            <p className="text-xs text-base-content/60">
              Tools for testing kiosk-only surfaces on a desktop browser. Virtual keyboard preference is saved on this device.
            </p>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium text-sm">On-screen keyboard</div>
                <div className="text-xs text-base-content/60">
                  Show the kiosk virtual keyboard when focusing text fields
                </div>
              </div>
              <input
                type="checkbox"
                className="toggle toggle-primary shrink-0"
                checked={forceVirtualKeyboard}
                onChange={toggleForceVirtualKeyboard}
                aria-label="Enable on-screen keyboard for testing"
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium text-sm">Sleep / lobby attract</div>
                <div className="text-xs text-base-content/60">
                  Fullscreen preview — dismiss with any mouse or keyboard input
                </div>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline shrink-0"
                onClick={() => {
                  onClose()
                  // Let the settings modal unmount before painting the overlay.
                  window.requestAnimationFrame(() => openSleepPreview())
                }}
              >
                Preview sleep screen
              </button>
            </div>
          </div>
        </div>
        {isAdmin && (
          <div className="mb-6">
            <label className="block font-semibold mb-2">Notification tests</label>
            <div className="rounded-lg border border-base-300 bg-base-100 p-4 space-y-3">
              <p className="text-xs text-base-content/60">
                Local OS notification tests. Browser permission is required. Use the 30-second option to background this tab before the toast appears.
              </p>
              {pushTestSuccess && <div className="alert alert-success py-2 px-3 text-sm">{pushTestSuccess}</div>}
              {pushTestError && <div className="alert alert-error py-2 px-3 text-sm">{pushTestError}</div>}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={pushTestBusy}
                  onClick={() => void handleSendTestPush(0)}
                >
                  {pushTestBusy ? 'Sending…' : 'Send push notification now'}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline"
                  disabled={pushTestBusy}
                  onClick={() => void handleSendTestPush(30000)}
                >
                  Send push notification in 30 seconds
                </button>
              </div>
            </div>
          </div>
        )}
          </>
        )}
        {activeCategory === 'security' && !kioskSettingsHost && (
          <>
        {/* Session & Security Timers */}
        <div className="mb-6">
          <label className="block font-semibold mb-2">Session & Security Timers</label>
          <div className="space-y-1 text-sm">
            <p><span className="font-medium">Session expires:</span> {formatRemaining(hardLogoutRemaining, 'No active session')}</p>
            <p><span className="font-medium">Inactivity logout:</span> {formatRemaining(inactivityRemaining, 'Unavailable')}</p>
            <p><span className="font-medium">MFA trusted device:</span> {formatRemaining(mfaRememberRemaining, 'Not enabled on this device')}</p>
          </div>
        </div>
        {/* Change Password */}
        <div className="mb-6">
          <label className="block font-semibold mb-2">Change Password</label>
          {success && <div className="alert alert-success mb-2 py-2 px-3">{success}</div>}
          {error && <div className="alert alert-error mb-2 py-2 px-3">{error}</div>}
          <form onSubmit={handleChangePassword} className="flex flex-col gap-2">
            <div>
              <div className="join w-full">
                <input
                  type={showCurrent ? 'text' : 'password'}
                  className="input input-bordered join-item w-full"
                  placeholder="Current password"
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="btn btn-ghost join-item"
                  onClick={() => setShowCurrent(v => !v)}
                  aria-label={showCurrent ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  <FontAwesomeIcon icon={showCurrent ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>
            <div>
              <div className="join w-full">
                <input
                  type={showNew ? 'text' : 'password'}
                  className="input input-bordered join-item w-full"
                  placeholder="New password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="btn btn-ghost join-item"
                  onClick={() => setShowNew(v => !v)}
                  aria-label={showNew ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  <FontAwesomeIcon icon={showNew ? faEyeSlash : faEye} />
                </button>
              </div>
              {newPassword && (
                <ul className="mt-2 text-xs space-y-1">
                  {passwordRequirements.map(req => (
                    <li key={req.label} className={req.test(newPassword) ? 'text-success' : 'text-error'}>
                      {req.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <div className="join w-full">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  className="input input-bordered join-item w-full"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="btn btn-ghost join-item"
                  onClick={() => setShowConfirm(v => !v)}
                  aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  <FontAwesomeIcon icon={showConfirm ? faEyeSlash : faEye} />
                </button>
              </div>
              {confirmPassword && newPassword !== confirmPassword && (
                <div className="text-error text-xs mt-1">Passwords do not match</div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <button className="btn btn-primary" type="submit" disabled={!canSubmit}>{loading ? 'Changing...' : 'Change Password'}</button>
            </div>
          </form>
        </div>
        {/* Experimental Features - hidden */}
        {false && (
        <div className="mb-6">
          <label className="block font-semibold mb-2">Experimental Features</label>
          <div className="form-control">
            <label className="label cursor-pointer">
              <span className="label-text text-base-content font-medium">AI Assistant</span>
              <input 
                type="checkbox" 
                className="toggle" 
                checked={aiAssistant}
                onChange={toggleAIAssistant}
              />
            </label>
            <p className="text-xs text-base-content/60 ml-4">Shows the AI assistant panel when enabled</p>
          </div>
        </div>
        )}

        {/* MFA Section */}
        <div className="mb-6">
          <label className="block font-semibold mb-2">Multi‑Factor Authentication</label>
          <div className="rounded-lg border border-base-300 bg-base-100 p-4 space-y-3">
            <div className="text-sm space-y-1">
              <p>Status: {mfaStatus?.enabled ? 'Enabled' : 'Setup required'}</p>
              <p>Recovery codes remaining: {mfaStatus?.remainingRecoveryCodes ?? 0}</p>
              <p>Passkeys enrolled: {passkeyCount}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button className="btn btn-sm btn-primary" onClick={async () => {
                try {
                  const res = await requestPasswordConfirm({
                    title: 'Confirm your password',
                    description: 'Re-enter your account password to regenerate MFA recovery codes.',
                    confirmLabel: 'Regenerate',
                    onConfirm: (currentPassword) => authService.mfaRegenSelf(currentPassword),
                  })
                  if (!res) return
                  setRecoveryCodes(res.recoveryCodes);
                  const st = await authService.mfaStatus();
                  setMfaStatus(st);
                  downloadRecoveryCodes(res.recoveryCodes);
                } catch (e: any) {
                  alert(e?.message || 'Failed to regenerate recovery codes');
                }
              }}>Regenerate Recovery Codes</button>
              <button className="btn btn-sm btn-outline" onClick={async () => {
                await authService.mfaPasskeyRegister();
                const [st, keys] = await Promise.all([authService.mfaStatus(), authService.mfaPasskeys()]);
                setMfaStatus(st);
                setPasskeys(keys);
              }}>{passkeysConfigured ? 'Add Passkey' : 'Set Up Passkey'}</button>
              {passkeysConfigured && (
                <button className="btn btn-sm btn-outline btn-warning" onClick={async () => {
                  if (!confirm('Reset all passkeys on your account? You will need to set up passkeys again if desired.')) return;
                  try {
                    const ok = await requestPasswordConfirm({
                      title: 'Confirm your password',
                      description: 'Re-enter your account password to reset all passkeys.',
                      confirmLabel: 'Reset passkeys',
                      onConfirm: async (currentPassword) => {
                        await authService.mfaResetPasskeys(currentPassword)
                        return true
                      },
                    })
                    if (!ok) return
                    const [st, keys] = await Promise.all([authService.mfaStatus(), authService.mfaPasskeys()]);
                    setMfaStatus(st);
                    setPasskeys(keys);
                  } catch (e: any) {
                    alert(e?.message || 'Failed to reset passkeys');
                  }
                }}>Reset Passkeys</button>
              )}
              {totpConfigured ? (
                <>
                  <button className="btn btn-sm btn-outline" onClick={async () => {
                    if (!confirm('Relink authenticator? This will replace your current MFA secret. Your recovery codes will be preserved.')) return;
                    setRelinking(true);
                    const s = await authService.mfaSetupStart().catch(() => null)
                    if (s && s.otpauth) { setMfaOtpauthURI(s.otpauth); setShowMfaModal(true) }
                  }}>Relink Authenticator</button>
                  <button className="btn btn-sm btn-outline btn-warning" onClick={async () => {
                    if (!confirm('Reset TOTP authenticator setup? This will remove your current authenticator app link and recovery codes.')) return;
                    try {
                      const ok = await requestPasswordConfirm({
                        title: 'Confirm your password',
                        description: 'Re-enter your account password to reset your authenticator.',
                        confirmLabel: 'Reset authenticator',
                        onConfirm: async (currentPassword) => {
                          await authService.mfaResetTotp(currentPassword)
                          return true
                        },
                      })
                      if (!ok) return
                      const st = await authService.mfaStatus();
                      setMfaStatus(st);
                      setRecoveryCodes(null);
                    } catch (e: any) {
                      alert(e?.message || 'Failed to reset authenticator');
                    }
                  }}>Reset Authenticator</button>
                </>
              ) : (
                <button className="btn btn-sm btn-outline" onClick={async () => {
                  setRelinking(false);
                  const s = await authService.mfaSetupStart().catch(() => null);
                  if (s && s.otpauth) { setMfaOtpauthURI(s.otpauth); setShowMfaModal(true); }
                  setMfaSetup(s);
                }}>Set Up Authenticator</button>
              )}
            </div>
            <div>
              <button className="btn btn-sm btn-outline btn-error" onClick={clearMfaCacheAndLogout}>
                Clear MFA Cache & Log Out
              </button>
            </div>
            <p className="text-xs text-warning">Keep recovery codes secure. Never share them with anyone other than the account owner.</p>
            {recoveryCodes && (
              <div className="mt-2">
                <p className="font-medium">Save these codes in a safe place (shown once):</p>
                <button className="btn btn-sm btn-outline mt-2" onClick={() => downloadRecoveryCodes(recoveryCodes)}>Download .txt</button>
                <ul className="list-disc ml-6">
                  {recoveryCodes.map(c => <li key={c}>{c}</li>)}
                </ul>
              </div>
            )}
            {!!passkeys.length && (
              <div className="mt-2 space-y-2">
                <p className="font-medium">Passkeys</p>
                {passkeys.map((p, idx) => (
                  <div key={p.credentialId} className="flex items-center justify-between text-sm border border-base-300 rounded px-2 py-1">
                    <span>Passkey {idx + 1}{p.lastUsedAt ? ` (last used ${new Date(p.lastUsedAt).toLocaleString()})` : ''}</span>
                    <button className="btn btn-xs btn-outline btn-error" onClick={async () => {
                      if (!confirm('Remove this passkey?')) return;
                      await authService.mfaDeletePasskey(p.credentialId);
                      const [st, keys] = await Promise.all([authService.mfaStatus(), authService.mfaPasskeys()]);
                      setMfaStatus(st);
                      setPasskeys(keys);
                    }}>Remove</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
          </>
        )}
        {/* Reuse MFA modal for setup/relink */}
        <MFAModal
          isOpen={showMfaModal}
          requiresSetup
          setupMethods={['totp']}
          otpauth={mfaOtpauthURI}
          onSetup={async (code) => {
            const res = await authService.mfaSetupVerify(code, undefined, true)
            if (res.recoveryCodes && res.recoveryCodes.length) setRecoveryCodes(res.recoveryCodes)
            if (res.recoveryCodes && res.recoveryCodes.length) downloadRecoveryCodes(res.recoveryCodes)
            const st = await authService.mfaStatus(); setMfaStatus(st)
            setShowMfaModal(false); setRelinking(false); setMfaSetup(null); setMfaCode('')
          }}
          onVerify={() => {}}
          onUseRecovery={() => {}}
          onClose={() => { setShowMfaModal(false); setRelinking(false) }}
        />
        {confirmPasswordModal}
            </div>
          </div>
        </div>
        <div className="shrink-0 border-t border-base-200 pb-safe md:hidden">
          <button
            type="button"
            className="btn btn-ghost min-h-12 h-12 w-full rounded-none text-base-content"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default SettingsModal 